import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button, ErrorAlert, Field, InfoField } from "src/components";
import DropdownAnimation from "src/components/UI/dropdownAnimation/DropdownAnimation";
import { useAlfredWithdrawal } from "src/hooks";
import { formatNumberToLocale } from "src/utils";
import AlfredWithdrawalInstructions from "./AlfredWithdrawalInstructions";
import AlfredAddBankAccountForm from "./AlfredAddBankAccountForm";

/**
 * Single-screen Alfred withdrawal form. Mirrors `DepositForm` in
 * structure: target-fiat picker → amount → payment-method picker →
 * fees breakdown → submit. Once the offramp is created, swaps the
 * form for {@link AlfredWithdrawalInstructions} (status panel).
 */
const AlfredWithdrawalForm: React.FC = (): JSX.Element => {
  const { t } = useTranslation();
  const w = useAlfredWithdrawal();
  const { currency_id, precision } = w.currency;

  const alfredFeeTotal = useMemo(() => {
    if (!w.quote) return 0;
    return w.quote.fees.reduce((sum, f) => sum + Number(f.amount), 0);
  }, [w.quote]);

  // Net fiat the user actually receives after Alfred's deduction
  // (already baked into `quote.toAmount`). Platform fee is taken on
  // the USDC side, so we show it next to the amount the user spends.
  const fiatReceiveNet = w.fiatReceiveGross;

  const dropdownOptions = useMemo(
    () =>
      w.options.map((opt) => ({
        key: opt.source_currency,
        value: `${opt.source_currency} — ${opt.rail}`,
      })),
    [w.options]
  );

  // FiatAccount labels: prefer the rail-specific accountAlias / accountName
  // when present, then fall back to a "type — ****last4" pattern derived
  // from accountNumber. Status filtering is intentionally absent — Alfred's
  // fiatAccount controller doesn't carry the same `pending/active`
  // gating as the legacy payment-methods one; if Alfred lists the
  // account, the widget shows it.
  const paymentMethodOptions = useMemo(
    () =>
      w.fiatAccounts.map((fa) => {
        const fields = fa.fiatAccountFields ?? {};
        const alias =
          (fields.accountAlias as string | undefined) ??
          (fields.accountName as string | undefined);
        const last4 = (fields.accountNumber as string | undefined)?.slice(-4);
        const label =
          alias ?? (last4 ? `${fa.type} — ****${last4}` : fa.type);
        return {
          key: fa.fiatAccountId,
          value: label,
        };
      }),
    [w.fiatAccounts]
  );

  const limitsHint = w.selectedOption
    ? t("DEPOSIT_FORM.LIMITS_HINT", {
        min: w.selectedOption.min_amount,
        max: w.selectedOption.max_amount,
        currency: currency_id,
      })
    : undefined;

  const firstError =
    w.errors.balanceErrors[0] ??
    w.errors.inputErrors[0] ??
    w.errors.paymentValidationErrors[0] ??
    w.errors.paymentMethodErrors[0] ??
    w.errors.paymentFeeErrors[0];

  if (w.transaction && w.sessionId) {
    return <AlfredWithdrawalInstructions sessionId={w.sessionId} transaction={w.transaction} onReset={w.resetWithdrawal} />;
  }

  if (!w.optionsLoading && w.options.length === 0) {
    return (
      <section className="deposit-form deposit-form--unavailable">
        <div className="deposit-form__unavailable-icon" aria-hidden>
          ✕
        </div>
        <h2 className="deposit-form__unavailable-title">{t("WITHDRAWAL_FORM.METHOD_NOT_AVAILABLE_TITLE")}</h2>
        <p className="deposit-form__unavailable-text">{t("WITHDRAWAL_FORM.METHOD_NOT_AVAILABLE_TEXT")}</p>
      </section>
    );
  }

  return (
    <form className="deposit-form" onSubmit={w.handleInitiateWithdrawal}>
      <DropdownAnimation
        label={t("WITHDRAWAL_FORM.RECEIVE_IN")}
        selectedValue={w.selectedCurrency}
        options={dropdownOptions}
        placeholder={t("DEPOSIT_FORM.SELECT_CURRENCY")}
        onChange={(field) => w.setSelectedCurrency(field.key)}
        disabled={w.optionsLoading || w.submitLoading}
      />

      <Field
        label={t("WITHDRAWAL_FORM.AMOUNT_IN_CURRENCY", { currency: currency_id })}
        value={w.amount}
        onChange={w.handleAmountChange}
        placeholder={limitsHint ?? t("DEPOSIT_FORM.AMOUNT_PLACEHOLDER_ZERO")}
        disabled={w.submitLoading || !w.selectedOption}
        error={w.amountTouched ? firstError : undefined}
        onBlur={() => w.setAmountTouched(true)}
      />

      {/* Dropdown + inline `+` icon to open the Add-bank-account form.
          The `+` lives outside DropdownAnimation (the third-party
          component owns its trailing chevron) so we render them in a
          flex row instead of trying to inject the icon into the input.
          Icon visibility is gated on a session existing — we need
          customerId resolved before we can POST a new fiatAccount. */}
      <div className="alfred-payment-method-row">
        <DropdownAnimation
          wrapperClass="alfred-payment-method-row__dropdown"
          label={t("WITHDRAWAL_FORM.PAYMENT_METHOD")}
          selectedValue={w.selectedFiatAccountId}
          options={paymentMethodOptions}
          placeholder={
            w.fiatAccountsLoading
              ? t("WITHDRAWAL_FORM.LOADING_METHODS")
              : paymentMethodOptions.length === 0
                ? t("WITHDRAWAL_FORM.NO_PAYMENT_METHODS")
                : t("WITHDRAWAL_FORM.SELECT_PAYMENT_METHOD")
          }
          onChange={(field) => w.setSelectedFiatAccountId(field.key)}
          disabled={w.fiatAccountsLoading || w.submitLoading || paymentMethodOptions.length === 0}
        />

        {w.sessionId && w.selectedOption && !w.addBankOpen && (
          <button
            type="button"
            className="alfred-payment-method-row__add"
            onClick={() => w.setAddBankOpen(true)}
            disabled={w.submitLoading}
            aria-label="Add bank account"
            title="Add bank account"
          >
            +
          </button>
        )}
      </div>

      {w.sessionId && w.selectedOption && w.addBankOpen && (
        <AlfredAddBankAccountForm
          sourceCurrency={w.selectedOption.source_currency}
          submitting={w.addBankLoading}
          error={w.addBankError}
          onSubmit={w.handleAddFiatAccount}
          onCancel={() => w.setAddBankOpen(false)}
        />
      )}

      <InfoField
        className="info-field--muted"
        label={t("DEPOSIT_FORM.PLATFORM_FEE")}
        value={`${formatNumberToLocale(w.paymentFee, precision)} ${currency_id}`}
        isLoading={w.paymentFeeLoading}
      />

      <InfoField
        className="info-field--muted"
        label={
          w.selectedOption ? t("DEPOSIT_FORM.ALFRED_FEES_VIA", { rail: w.selectedOption.rail }) : t("DEPOSIT_FORM.ALFRED_FEES")
        }
        value={w.quote ? `${formatNumberToLocale(alfredFeeTotal, 2)} ${w.quote.toCurrency}` : "—"}
        isLoading={w.quoteLoading}
      />

      <InfoField
        label={t("WITHDRAWAL_FORM.RECEIVE_TOTAL")}
        value={
          w.quote && w.selectedOption ? `${formatNumberToLocale(fiatReceiveNet, 2)} ${w.selectedOption.source_currency}` : "—"
        }
        isLoading={w.quoteLoading}
      />

      <ErrorAlert message={w.submitError} />

      <Button
        title={t("WITHDRAWAL_FORM.WITHDRAW")}
        className="deposit-button"
        type="submit"
        disabled={w.submitDisabled}
        pending={w.submitLoading}
        tooltipMessage={Object.values(w.errors).flat()[0]}
      />
    </form>
  );
};

export default AlfredWithdrawalForm;
